import { Building2, HardHat, Users, Wrench, type LucideIcon } from 'lucide-react';
import type { Acces } from '@/shared/auth/types';
import { depots } from '@/shared/donnees';
import { navigationPour } from '../navigation';

export interface ResultatRecherche {
  id: string;
  libelle: string;
  detail: string;
  icone: LucideIcon;
  chemin: string;
  groupe: string;
}

/**
 * Une source de la recherche globale.
 *
 * En S1, seule la navigation répond. Chaque module qui arrive (partenaires en
 * S2, chantiers en S3…) ajoute sa source ici, sans toucher à la palette.
 */
export interface SourceRecherche {
  groupe: string;
  rechercher(
    requete: string,
    role: Acces,
    ceo: boolean
  ): ResultatRecherche[] | Promise<ResultatRecherche[]>;
}

/** Minuscules, sans accents, espaces resserrés : « Été » et « ete » se trouvent. */
export function normaliser(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Chaque mot de la requête doit apparaître quelque part dans les textes. */
export function correspond(requete: string, textes: readonly string[]): boolean {
  const mots = normaliser(requete).split(' ').filter(Boolean);
  if (mots.length === 0) return true;
  const botte = normaliser(textes.join(' '));
  return mots.every((mot) => botte.includes(mot));
}

export const sourceNavigation: SourceRecherche = {
  groupe: 'Aller à',
  rechercher(requete, role, ceo) {
    return navigationPour(role, ceo)
      .flatMap((g) => g.entrees.map((e) => ({ entree: e, groupe: g.libelle })))
      .filter(({ entree, groupe }) =>
        correspond(requete, [
          entree.libelle,
          groupe,
          entree.description,
          ...(entree.motsCles ?? []),
        ])
      )
      .map(({ entree, groupe }) => ({
        id: `nav-${entree.id}`,
        libelle: entree.libelle,
        detail: groupe,
        icone: entree.icone,
        chemin: entree.chemin,
        groupe: 'Aller à',
      }));
  },
};

/* Une liste lue une fois par ouverture de la palette suffit : on ne relit pas
   la base à chaque lettre tapée. */
function enCache<T>(lire: () => Promise<T>, dureeMs = 30_000): () => Promise<T> {
  let valeur: { quand: number; promesse: Promise<T> } | null = null;
  return () => {
    if (!valeur || Date.now() - valeur.quand > dureeMs) {
      valeur = { quand: Date.now(), promesse: lire() };
      valeur.promesse.catch(() => {
        valeur = null;
      });
    }
    return valeur.promesse;
  };
}

const LIMITE = 6;
const entreprisesEnCache = enCache(() => depots().entreprises.lister());
const partenairesEnCache = enCache(() => depots().partenaires.lister());

export const sourceEntreprises: SourceRecherche = {
  groupe: 'Entreprises',
  async rechercher(requete, role) {
    if (role !== 'bureau' || normaliser(requete) === '') return [];
    const toutes = await entreprisesEnCache().catch(() => []);
    return toutes
      .filter((e) =>
        correspond(requete, [
          e.raisonSociale,
          e.commune,
          e.codePostal,
          e.siret.replace(/\s/g, ''),
          e.activite,
        ])
      )
      .slice(0, LIMITE)
      .map((e) => ({
        id: `entreprise-${e.id}`,
        libelle: e.raisonSociale,
        detail: [e.commune, `${e.nbSites} site${e.nbSites > 1 ? 's' : ''}`]
          .filter(Boolean)
          .join(' · '),
        icone: Building2,
        chemin: `/entreprises/${e.id}`,
        groupe: 'Entreprises',
      }));
  },
};

export const sourcePartenaires: SourceRecherche = {
  groupe: 'Partenaires',
  async rechercher(requete, role) {
    if (role !== 'bureau' || normaliser(requete) === '') return [];
    const tous = await partenairesEnCache().catch(() => []);
    return tous
      .filter((p) => correspond(requete, [p.raisonSociale, p.contactNom, p.commune, ...p.zones]))
      .slice(0, LIMITE)
      .map((p) => ({
        id: `partenaire-${p.id}`,
        libelle: p.raisonSociale,
        detail: [p.contactNom, p.commune].filter(Boolean).join(' · '),
        icone: Users,
        chemin: `/partenaires/${p.id}`,
        groupe: 'Partenaires',
      }));
  },
};

/* On cherche aussi un vieux chantier par sa référence : les archives comprises. */
const chantiersEnCache = enCache(() => depots().chantiers.lister({ avecArchives: true }));

export const sourceChantiers: SourceRecherche = {
  groupe: 'Chantiers',
  async rechercher(requete, role) {
    if (role !== 'bureau' || normaliser(requete) === '') return [];
    const tous = await chantiersEnCache().catch(() => []);
    return tous
      .filter((c) =>
        correspond(requete, [
          c.reference,
          c.entrepriseNom,
          c.siteNom,
          c.commune,
          c.departement,
          c.titre,
        ])
      )
      .slice(0, LIMITE)
      .map((c) => ({
        id: `chantier-${c.id}`,
        libelle: `${c.reference} · ${c.entrepriseNom}`,
        detail: [c.titre, `${c.commune}${c.departement ? ` (${c.departement})` : ''}`]
          .filter(Boolean)
          .join(' · '),
        icone: HardHat,
        chemin: `/chantiers/${c.id}`,
        groupe: 'Chantiers',
      }));
  },
};

const ticketsEnCache = enCache(() => depots().sav.lister());

export const sourceSav: SourceRecherche = {
  groupe: 'SAV',
  async rechercher(requete, role) {
    if (role !== 'bureau' || normaliser(requete) === '') return [];
    const tous = await ticketsEnCache().catch(() => []);
    return tous
      .filter((t) =>
        correspond(requete, [
          t.reference,
          t.objet,
          t.entrepriseNom,
          t.commune,
          t.chantierReference,
          t.partenaireNom ?? '',
        ])
      )
      .slice(0, LIMITE)
      .map((t) => ({
        id: `sav-${t.id}`,
        libelle: `${t.reference} · ${t.objet}`,
        detail: [t.entrepriseNom, t.commune].filter(Boolean).join(' · '),
        icone: Wrench,
        chemin: `/sav/${t.id}`,
        groupe: 'SAV',
      }));
  },
};

export const SOURCES: readonly SourceRecherche[] = [
  sourceNavigation,
  sourceChantiers,
  sourceSav,
  sourceEntreprises,
  sourcePartenaires,
];

export async function rechercher(
  requete: string,
  role: Acces,
  ceo: boolean,
  sources: readonly SourceRecherche[] = SOURCES
): Promise<ResultatRecherche[]> {
  const lots = await Promise.all(
    sources.map((s) => Promise.resolve(s.rechercher(requete, role, ceo)))
  );
  return lots.flat();
}
